/**
 * Students management page.
 * Tabbed list with {@link CollapseReplaceWrapper} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation, useSearchParams, useNavigationType } from 'react-router-dom';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { StudentsDataTable } from '@/components/students/remote/StudentsDataTable';
import { CreateStudentView } from '@/components/students/remote/CreateStudentView';
import { EditStudentView } from '@/components/students/remote/EditStudentView';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';
import { usePermissions } from '@/hooks/usePermissions';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/forms/Button';
import { onboardingKeys } from '@/hooks/useUsers';
import { exportExcel } from '@/services/remote/students.service';
import * as Icons from 'lucide-react';
import {
  type StudentRemoteTabView as TabView,
  STUDENT_REMOTE_TAB_LABELS as TAB_LABELS,
  STUDENT_REMOTE_TAB_COLORS as TAB_COLORS,
  STUDENT_REMOTE_TAB_ICONS as TAB_ICONS,
} from '@/components/students/student-configs';
import type { OnboardingFilters } from '@/types/remote/student.types';

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'create' | 'edit' | null;

/** Remote students list with CollapseReplace create/edit. */
export function StudentsManagementPage() {
  const { canCreate, canEditRow, canExportExcel, canArchiveRow, canReactivateRow } =
    usePageActions('students');
  const { canViewInterface } = usePermissions();
  /** Subscribe so store updates re-render when session / report visibility changes. */
  useInterfaceElements('sessions');
  useInterfaceElements('reports');
  const { elements: sidebarElements } = useInterfaceElements('sidebar');
  const canViewSessions = canViewInterface('sessions');
  const canViewReports = canViewInterface('reports');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';

  const navigationType = useNavigationType();
  /** Restore the last tab on browser back (`POP`); otherwise default to `students`. */
  const [activeTab, setActiveTab] = React.useState<TabView>(() => {
    if (navigationType === 'POP') {
      const saved = sessionStorage.getItem('dt-tab:students') as TabView | null;
      if (saved) return saved;
    }
    return 'students';
  });
  React.useEffect(() => {
    sessionStorage.setItem('dt-tab:students', activeTab);
  }, [activeTab]);
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editUserId, setEditUserId] = React.useState<string | null>(null);
  const [editStepKey, setEditStepKey] = React.useState<string | undefined>(undefined);

  const { viewMode, setViewMode } = useViewMode('students-management');
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const [searchParams, setSearchParams] = useSearchParams();

  /** Download the current filtered list as xlsx (blob URL is revoked after click). */
  const handleExportExcel = async (
    filters: Omit<OnboardingFilters, 'companyId' | 'page' | 'pageSize'>,
  ) => {
    try {
      const blob = await exportExcel(filters, companyId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eleves-remote-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  /** Deep-link: filter the list by parent (`linkedUserId`) passed via location.state. */
  const [parentFilter, setParentFilter] = React.useState<{
    linkedUserId?: string;
    personName?: string;
  }>(() => {
    const state = location.state as {
      linkedUserId?: string;
      personName?: string;
    } | null;
    return {
      linkedUserId: state?.linkedUserId,
      personName: state?.personName,
    };
  });

  React.useEffect(() => {
    const state = location.state as {
      linkedUserId?: string;
      personName?: string;
    } | null;
    if (state?.linkedUserId) {
      setParentFilter({
        linkedUserId: state.linkedUserId,
        personName: state.personName,
      });
      void navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  /** Deep-link: expand a student row via `?view=`, then strip the param. */
  React.useEffect(() => {
    const viewId = searchParams.get('view');
    if (viewId) {
      setOpenRow(viewId);
      searchParams.delete('view');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  /** Close the form and drop the edit record / step so the next open starts clean. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditUserId(null);
    setEditStepKey(undefined);
  }, []);

  /** Close the form and refresh onboarding lists. */
  const handleFormSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditUserId(null);
    setEditStepKey(undefined);
    void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
  }, [queryClient]);

  /** Stable JSX node for the form — avoids unmounting {@link EditStudentView} on re-renders. */
  const formContent = React.useMemo<React.ReactNode>(() => {
    if (activeForm === 'create') {
      return <CreateStudentView onSuccess={handleFormSuccess} onClose={closeForm} />;
    }
    if (activeForm === 'edit' && editUserId) {
      return (
        <EditStudentView
          key={editUserId}
          userId={editUserId}
          stepKey={editStepKey}
          onSuccess={handleFormSuccess}
          onClose={closeForm}
        />
      );
    }
    return null;
  }, [activeForm, editUserId, editStepKey, handleFormSuccess, closeForm]);

  const studentTabItems = React.useMemo(() => {
    const IconsMap = Icons as unknown as Record<string, Icons.LucideIcon>;
    const menuEl = sidebarElements.find((el) => el.elementId === 'menu-students');
    const MainIcon = IconsMap[menuEl?.config?.['iconName'] as string] ?? Icons.Circle;
    return (Object.keys(TAB_LABELS) as TabView[]).map((id) => {
      const Icon = id === 'students' ? MainIcon : TAB_ICONS[id];
      return {
        id,
        label: TAB_LABELS[id],
        icon: <Icon className="w-3 h-3" />,
        color: TAB_COLORS[id],
      };
    });
  }, [sidebarElements]);

  return (
    <ManagementPage
      title="Gestion des Élèves"
      subtitle="Créez, modifiez et gérez tous vos élèves"
      tabs={{
        items: studentTabItems,
        activeTab,
        onTabChange: (id) => {
          closeForm();
          closeRow();
          setActiveTab(id as TabView);
        },
      }}
      actions={
        canCreate ? (
          <Button
            preset="create-entity"
            onClick={() => {
              closeRow();
              setActiveForm('create');
            }}
          >
            Créer un élève
          </Button>
        ) : undefined
      }
    >
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        {/** Banner shown when the list is filtered to one parent's students. */}
        {parentFilter.personName && (
          <div className="mb-4 p-3 rounded-lg bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] border border-[var(--color-2-nuance-1)] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium" style={{ color: 'var(--color-2-nuance-1)' }}>
                Élèves de {parentFilter.personName}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setParentFilter({ linkedUserId: undefined, personName: undefined });
                void navigate(location.pathname, { replace: true, state: {} });
              }}
            >
              Réinitialiser
            </Button>
          </div>
        )}

        <StudentsDataTable
          companyId={companyId}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          expandedRowId={openRowId}
          onToggleRow={openRow}
          onCloseDetails={closeRow}
          onEditStudent={(id) => {
            setEditUserId(id);
            setEditStepKey('registration_information');
            setActiveForm('edit');
            closeRow();
          }}
          onViewSessions={(record) => {
            void navigate('/sessions', {
              state: {
                studentUserId: record.user?.id,
                personName: `${record.user?.firstName ?? ''} ${record.user?.lastName ?? ''}`.trim(),
              },
            });
          }}
          onViewReports={(record) => {
            void navigate('/reports', {
              state: {
                studentUserId: record.user?.id,
                personName: `${record.user?.firstName ?? ''} ${record.user?.lastName ?? ''}`.trim(),
              },
            });
          }}
          onViewParent={(parentUserId) => {
            void navigate('/parents', {
              state: {
                openParentUserId: parentUserId,
              },
            });
          }}
          enableFilters={true}
          activeTab={activeTab}
          pageActions={{
            canEditRow,
            canViewRow: true,
            canViewSessions,
            canViewReports,
            canArchiveRow,
            canReactivateRow,
          }}
          initialFilters={{
            parentId: parentFilter.linkedUserId,
          }}
          showExport={canExportExcel}
          onExportExcel={handleExportExcel}
          onEditSection={(stepKey, record) => {
            setEditUserId(record.userId);
            setEditStepKey(stepKey);
            setActiveForm('edit');
          }}
        />
      </CollapseReplaceWrapper>
    </ManagementPage>
  );
}
